import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

const API_URL = import.meta.env.VITE_API_URL || ''

interface AuthUser {
  id: string
  email: string
  name: string
  role: string
  businessIds: string[]
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  operator: AuthUser | null
  token: string | null
  login: (email: string, password?: string) => Promise<void>
  logout: () => Promise<void>
  getAuthHeaders: () => Record<string, string>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function getStoredToken(): string | null {
  try {
    return localStorage.getItem('auth_token')
  } catch {
    return null
  }
}

function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem('auth_user')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(getStoredToken)
  const [operator, setOperator] = useState<AuthUser | null>(getStoredUser)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!token && !!operator

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }, [token])

  // Check for token in URL (from WorkOS OAuth callback) and validate on mount
  useEffect(() => {
    const validateSession = async () => {
      // Check for token in URL params (from OAuth redirect)
      const urlParams = new URLSearchParams(window.location.search)
      const urlToken = urlParams.get('token')

      if (urlToken) {
        // Store the token from URL and clean up URL
        localStorage.setItem('auth_token', urlToken)
        // Remove token from URL without reload
        const cleanUrl = window.location.pathname
        window.history.replaceState({}, '', cleanUrl)
      }

      const storedToken = urlToken || getStoredToken()
      if (!storedToken) {
        setIsLoading(false)
        return
      }
      try {
        const res = await fetch(`${API_URL}/api/operator/auth/me`, {
          headers: { 'Authorization': `Bearer ${storedToken}` },
        })
        if (res.ok) {
          const data = await res.json()
          const user = data.operator || data.data || data.user
          localStorage.setItem('auth_user', JSON.stringify(user))
          setOperator(user)
          setToken(storedToken)
        } else {
          localStorage.removeItem('auth_token')
          localStorage.removeItem('auth_user')
          setToken(null)
          setOperator(null)
        }
      } catch {
        // Keep existing session if network error
      } finally {
        setIsLoading(false)
      }
    }
    validateSession()
  }, [])

  const login = useCallback(async (email: string, password?: string) => {
    const res = await fetch(`${API_URL}/api/operator/auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Login failed' }))
      throw new Error(err.message || 'Login failed')
    }
    const data = await res.json()
    const newToken = data.token || data.accessToken
    const user = data.operator || data.user
    if (newToken) {
      localStorage.setItem('auth_token', newToken)
      localStorage.setItem('auth_user', JSON.stringify(user))
      setToken(newToken)
      setOperator(user)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/api/operator/auth`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        }).catch(() => {})
      }
    } finally {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
      sessionStorage.clear()
      setToken(null)
      setOperator(null)
      window.location.href = '/sign-in'
    }
  }, [token])

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, operator, token, login, logout, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
